import type React from 'react';
import {Box, Checkbox, FormControlLabel, Stack, Typography} from '@mui/material';
import type {PlanProps} from '../strategyProps';

const R_OUT = 130;
const R_IN = 62;

function arc(i: number, n: number): string {
    const a0 = (i / n) * 2 * Math.PI - Math.PI / 2;
    const a1 = ((i + 1) / n) * 2 * Math.PI - Math.PI / 2;
    const p = (r: number, a: number) => `${150 + r * Math.cos(a)} ${150 + r * Math.sin(a)}`;
    return `M ${p(R_OUT, a0)} A ${R_OUT} ${R_OUT} 0 0 1 ${p(R_OUT, a1)} L ${p(R_IN, a1)} A ${R_IN} ${R_IN} 0 0 0 ${p(R_IN, a0)} Z`;
}

/** Sourcing wheel (A22): twelve sourcing options; those that suit the Kraljic quadrant are marked. */
export const SourcingWheelTool: React.FC<PlanProps> = ({plan, actions}) => {
    const {content, derived, reference, canEdit} = plan;
    const options = reference.wheel;
    const suited = new Set(derived.wheelSuited);
    const toggle = (key: string) => {
        const next = content.wheel.includes(key) ? content.wheel.filter(k => k !== key) : [...content.wheel, key];
        void actions.save('wheel', next, 'Sourcing options updated.');
    };

    return (
        <Stack direction={{xs: 'column', md: 'row'}} gap={3} alignItems="flex-start">
            <svg viewBox="0 0 300 300" width={300} height={300} role="group" aria-label="Sourcing wheel">
                {options.map((o, i) => {
                    const selected = content.wheel.includes(o.key);
                    const mid = ((i + 0.5) / options.length) * 2 * Math.PI - Math.PI / 2;
                    const r = (R_OUT + R_IN) / 2;
                    return (
                        <g key={o.key} onClick={() => canEdit && toggle(o.key)} style={{cursor: canEdit ? 'pointer' : 'default'}}>
                            <title>{`${o.label}${suited.has(o.key) ? ' (suits this quadrant)' : ''}`}</title>
                            <path d={arc(i, options.length)} fill={selected ? '#2D6FF5' : suited.has(o.key) ? '#DCE6FD' : '#F1F2F4'}
                                  stroke="#fff" strokeWidth={2}/>
                            <text x={150 + r * Math.cos(mid)} y={150 + r * Math.sin(mid)} fontSize={9} textAnchor="middle"
                                  dominantBaseline="middle" fill={selected ? '#fff' : '#1F2229'}>
                                {o.label.length > 14 ? `${o.label.slice(0, 13)}…` : o.label}
                            </text>
                        </g>
                    );
                })}
                <text x={150} y={146} textAnchor="middle" fontSize={12} fontWeight={600} fill="#1F2229">{derived.kraljic.quadrant}</text>
                <text x={150} y={162} textAnchor="middle" fontSize={10} fill="#7A7F8A">{`${content.wheel.length} selected`}</text>
            </svg>
            <Box sx={{columns: {xs: 1, sm: 2}, flex: 1}}>
                {options.map(o => (
                    <FormControlLabel key={o.key} sx={{display: 'flex', breakInside: 'avoid'}} disabled={!canEdit}
                                      control={<Checkbox size="small" checked={content.wheel.includes(o.key)} onChange={() => toggle(o.key)}/>}
                                      label={
                                          <Typography variant="body2">
                                              {o.label}
                                              {suited.has(o.key) && <Typography component="span" variant="caption" color="primary.main"> · suits {derived.kraljic.quadrant}</Typography>}
                                          </Typography>
                                      }/>
                ))}
            </Box>
        </Stack>
    );
};
